// DochadzkaAlarm — widget/pás dochádzky pre hodinovkárov (Mirko 12. 9. 2026): bez zápisu príchodu bliká a pípa.
// Desktop: <DochadzkaCard /> (karta v hornom rade), mobil: <DochadzkaBanner onOpen /> (pás nad obsahom).
// Pípanie: WebAudio po prvom dotyku/kliknutí (prehliadač inak zvuk nepustí), každých 30 s; mobil aj vibruje.
import React, { useEffect, useRef, useState } from 'react'
import { fetchAuth } from '../auth/fetchAuth.js'

export function useDochadzka(pollMs = 60000) {
  const [d, setD] = useState(null)
  useEffect(() => {
    let alive = true
    const load = () => fetchAuth('/api/hodinovkari/dnes').then(r => r.ok ? r.json() : null).then(x => { if (alive) setD(x) }).catch(() => {})
    load(); const id = setInterval(load, pollMs)
    return () => { alive = false; clearInterval(id) }
  }, [pollMs])
  return d
}

// pípanie + vibrácia, kým platí `alarm`
function useAlarmSound(alarm) {
  const ctxRef = useRef(null); const armed = useRef(false)
  useEffect(() => {
    if (!alarm) return
    const beep = () => {
      try {
        if (!armed.current) return
        const Ctx = window.AudioContext || window.webkitAudioContext; if (!Ctx) return
        if (!ctxRef.current) ctxRef.current = new Ctx()
        const ctx = ctxRef.current; if (ctx.state === 'suspended') ctx.resume()
        for (let i = 0; i < 3; i++) {
          const o = ctx.createOscillator(); const g = ctx.createGain()
          o.type = 'square'; o.frequency.value = 880; g.gain.value = 0.08
          o.connect(g); g.connect(ctx.destination)
          const t0 = ctx.currentTime + i * 0.35; o.start(t0); o.stop(t0 + 0.2)
        }
        if (navigator.vibrate) navigator.vibrate([300, 120, 300, 120, 300])
      } catch (_) {}
    }
    const arm = () => { armed.current = true; beep() }
    window.addEventListener('pointerdown', arm, { once: true }); window.addEventListener('keydown', arm, { once: true })
    const id = setInterval(beep, 30000)
    return () => { clearInterval(id); window.removeEventListener('pointerdown', arm); window.removeEventListener('keydown', arm) }
  }, [alarm])
}

const KEYFRAMES = `@keyframes kamirBlik { 0%,100% { background:#dc2626; box-shadow:0 0 0 0 rgba(220,38,38,.7) } 50% { background:#7f1d1d; box-shadow:0 0 0 12px rgba(220,38,38,0) } }`

export function DochadzkaCard({ onOpen, cardStyle }) {
  const d = useDochadzka()
  const me = d && d.me; const workday = !d || d.workday !== false; const alarm = !!(me && me.hourly && me.linked && !me.punched && workday)
  useAlarmSound(alarm)
  if (!d) return null
  const team = d.team || []
  if (!(me && me.hourly) && !team.length) return null
  const missing = team.filter(t => t.hourly && t.linked && !t.punched)
  const unlinked = team.filter(t => !t.linked)
  if (me && me.hourly && !me.linked) {
    // Mirko 13. 9. 2026: „väzba je nutná“ — konto bez zamestnanca z číselníka nevie mať dochádzku.
    // Červená bez blikania a bez pípania: opraví to admin (Používatelia › Zamestnanec), nie hodinovkár.
    return (
      <div onClick={onOpen} title="Tvoje konto nie je prepojené so zamestnancom z číselníka. Nastaví to admin v Nastavenia › Používatelia (pole Zamestnanec)."
        style={{ ...(cardStyle || {}), cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, color: '#fff', background: '#b91c1c' }}>
        <div style={{ fontSize: 13, fontWeight: 600, opacity: .92 }}>Dochádzka dnes</div>
        <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15 }}>BEZ VÄZBY</div>
        <div style={{ fontSize: 13, opacity: .92 }}>konto nie je prepojené so zamestnancom, povedz adminovi</div>
      </div>
    )
  }
  if (me && me.hourly) {
    // Mirko 12. 9.: hodinovkár vidí svoj widget ZELENÝ, keď má dochádzku, ČERVENÝ (bliká + pípa), keď nie
    const hhmm = (x) => (x ? String(x).replace('T', ' ').slice(11, 16) : '')
    return (
      <div onClick={onOpen} title={alarm ? 'Nemáš zapísaný príchod. Klikni a zapíš dochádzku, inak to bude blikať a pípať.' : 'Dochádzka dnes je zapísaná. Klik otvorí Dochádzku (odchod, oprava).'}
        style={{ ...(cardStyle || {}), cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, color: '#fff',
          background: alarm ? undefined : (me.punched ? '#16a34a' : '#6b7280'), animation: alarm ? 'kamirBlik 1s infinite' : 'none' }}>
        <style>{KEYFRAMES}</style>
        <div style={{ fontSize: 13, fontWeight: 600, opacity: .92 }}>Dochádzka dnes</div>
        <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15 }}>{alarm ? 'CHÝBA' : (!me.punched ? 'víkend' : (me.end_at ? 'OK, hotovo' : 'OK'))}</div>
        <div style={{ fontSize: 13, opacity: .92 }}>{alarm ? 'klikni a zapíš príchod' : (!me.punched ? 'dnes sa dochádzka nesleduje' : ('príchod ' + hhmm(me.start_at) + (me.end_at ? ' · odchod ' + hhmm(me.end_at) : '')))}</div>
      </div>
    )
  }
  return (
    <div onClick={onOpen} title="Kto z hodinovkárov má dnes zapísanú dochádzku. Klik otvorí Dochádzku."
      style={{ ...(cardStyle || {}), cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Dochádzka hodinovkári</div>
      <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15, color: (workday && missing.length) ? '#dc2626' : '#111827' }}>{team.filter(t => t.punched).length}/{team.filter(t => t.linked).length}</div>
      <div style={{ fontSize: 13, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {!workday ? 'víkend, dochádzka sa nesleduje' : (missing.length ? 'chýba: ' + missing.map(t => t.name.split(' ')[0]).join(', ') : 'všetci zapísaní')}{unlinked.length ? ' · bez prepojenia: ' + unlinked.map(t => t.name.split(' ')[0]).join(', ') : ''}
      </div>
    </div>
  )
}

export function DochadzkaBanner({ onOpen }) {
  const d = useDochadzka(45000)
  const me = d && d.me; const workday = !d || d.workday !== false; const alarm = !!(me && me.hourly && me.linked && !me.punched && workday)
  useAlarmSound(alarm)
  if (!(me && me.hourly)) return null
  if (!me.linked) {
    return (
      <div onClick={onOpen} style={{ padding: '6px 14px', background: '#b91c1c', color: '#fff', fontWeight: 700, fontSize: 13, textAlign: 'center', cursor: 'pointer' }}>
        Konto nie je prepojené so zamestnancom. Dochádzku ti zapne admin (Používatelia › Zamestnanec).
      </div>
    )
  }
  if (!alarm && !me.punched) return null
  if (!alarm) {
    const hhmm = (x) => (x ? String(x).replace('T', ' ').slice(11, 16) : '')
    return (
      <div onClick={onOpen} style={{ padding: '6px 14px', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 13, textAlign: 'center', cursor: 'pointer' }}>
        Dochádzka OK · príchod {hhmm(me.start_at)}{me.end_at ? ' · odchod ' + hhmm(me.end_at) : ''}
      </div>
    )
  }
  return (
    <div onClick={onOpen} role="alert" style={{ padding: '10px 14px', color: '#fff', fontWeight: 800, fontSize: 15, textAlign: 'center', cursor: 'pointer', animation: 'kamirBlik 1s infinite' }}>
      <style>{KEYFRAMES}</style>
      NEMÁŠ ZAPÍSANÚ DOCHÁDZKU. Klikni sem a zapíš príchod.
    </div>
  )
}
